import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Users, Search, GraduationCap, Eye, KeyRound, Pencil, QrCode,
  UserCheck, UserX, ShieldAlert, History, Loader2, Hash, Trash2, Home, ClipboardCheck, HeartPulse,
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';
import StudentDetailDialog from '@/components/students/StudentDetailDialog';
import StudentAccountInfoDialog from '@/components/students/StudentAccountInfoDialog';
import { confirmDialog } from '@/components/ui/confirm-dialog';
import UnduhDataSiswaButton from '@/components/students/UnduhDataSiswaButton';
import KelengkapanPopover from '@/components/students/KelengkapanPopover';
import ExportTemplateButton from '@/components/students/ExportTemplateButton';
import { KOLOM_DATA_SISWA } from '@/lib/dataSiswaKolom';
import ImportDataMasterButton from '@/components/students/ImportDataMasterButton';
import { formatWIB } from '@/lib/datetime';
import { VIEW_CONTEXT_CHANGED } from '@/lib/useViewYear';

export default function DataSiswaPage() {
  const { activeRole, user } = useAuth();
  const isAdmin = activeRole === 'admin' || user?.roles?.includes('admin');
  const isWaliKelas = activeRole === 'wali_kelas' || user?.roles?.includes('wali_kelas');
  // These roles see the same all-students admin-style view/filters, but
  // without any create/edit/delete/account actions (view-only).
  const VIEW_ONLY_BROAD_ROLES = ['guru_tata_tertib', 'unit_pelayanan', 'guru_bk', 'kepala_sekolah', 'waka_kesiswaan', 'penjamin_mutu'];
  const hasAdminView = isAdmin || VIEW_ONLY_BROAD_ROLES.includes(activeRole);
  const homeroomClassId = user?.homeroom_class_id;
  const canEdit = isAdmin; // Only admin can edit/delete students
  const canViewAccount = isAdmin || isWaliKelas; // Admin and wali kelas can view account info
  // Riwayat UKS (kunjungan & CKG) boleh dilihat admin, wali kelas, kepala sekolah, dan petugas UKS.
  const canViewRiwayatUKS = isAdmin || isWaliKelas || ['kepala_sekolah', 'unit_kesehatan'].includes(activeRole);

  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('all');
  const [tingkatFilter, setTingkatFilter] = useState('all');
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [genderFilter, setGenderFilter] = useState('all');
  // Muat ulang saat pengguna mengganti semester di Mode Lihat (data siswa & rombel per semester).
  const [muatUlang, setMuatUlang] = useState(0);
  useEffect(() => {
    const h = () => { setSelectedClass('all'); setMuatUlang((n) => n + 1); };
    window.addEventListener(VIEW_CONTEXT_CHANGED, h);
    return () => window.removeEventListener(VIEW_CONTEXT_CHANGED, h);
  }, []);

  const [detailStudent, setDetailStudent] = useState(null);
  const [accountStudent, setAccountStudent] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        if (hasAdminView) {
          // Get active academic year first
          const ayRes = await api.get('/academic-years/active');
          const activeAY = ayRes.data;

          // Load classes for active academic year
          const c = await api.get('/classes', {
            params: activeAY ? { academic_year_id: activeAY.id } : {}
          });
          setClasses(c.data || []);
          await loadStudents('all');
        } else if (isWaliKelas) {
          // Wali kelas: server memilih rombel perwalian pada semester yang sedang dilihat.
          if (homeroomClassId) setSelectedClass(homeroomClassId);
          await loadStudents('all');
        }
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAdminView, isWaliKelas, homeroomClassId, muatUlang]);

  const loadStudents = async (classId) => {
    setLoading(true);
    try {
      // Data Siswa hanya menampilkan siswa aktif (termasuk mutasi masuk); mutasi keluar ada di menu Mutasi.
      const params = { exclude_mutation: true };
      if (classId && classId !== 'all') params.class_id = classId;
      const { data } = await api.get('/students', { params });
      setStudents(data || []);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (hasAdminView) {
      loadStudents(selectedClass);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass]);

  const tingkatOptions = [...new Set(classes.map((c) => c.grade).filter((g) => g !== undefined && g !== null))].sort((a, b) => a - b);
  const classIdToGrade = classes.reduce((acc, c) => { acc[c.id] = c.grade; return acc; }, {});
  const kelasMap = classes.reduce((acc, c) => { acc[c.id] = { name: c.name, grade: c.grade }; return acc; }, {});

  const filtered = students.filter((s) => {
    if (search && !s.full_name?.toLowerCase().includes(search.toLowerCase()) && !s.nisn?.includes(search)) return false;
    if (genderFilter !== 'all' && s.gender !== genderFilter) return false;
    if (tingkatFilter !== 'all' && String(classIdToGrade[s.student_class_id]) !== String(tingkatFilter)) return false;
    return true;
  });

  const refresh = () => loadStudents(selectedClass);

  // Per-grade (kelas 7/8/9) stat overview, based on all loaded students
  // regardless of the table's active filters.
  const gradeStats = tingkatOptions.map((grade) => {
    const inGrade = students.filter((s) => String(classIdToGrade[s.student_class_id]) === String(grade));
    const total = inGrade.length;
    const laki = inGrade.filter((s) => s.gender === 'L').length;
    const perempuan = inGrade.filter((s) => s.gender === 'P').length;
    const mahad = inGrade.filter((s) => s.santri_mahad).length;
    const avgCompleteness = total > 0
      ? Math.round(inGrade.reduce((sum, s) => sum + (s.completeness_percentage ?? 0), 0) / total)
      : 0;
    return { grade, total, laki, perempuan, mahad, avgCompleteness };
  });

  // Jumlah siswa per tingkat (dari semua siswa termuat) untuk pratinjau dialog unduhan.
  // Hanya akurat bila semua kelas termuat (filter kelas = semua).
  const jumlahPerTingkat = selectedClass === 'all'
    ? gradeStats.reduce((acc, g) => ({ ...acc, [String(g.grade)]: g.total }), { all: students.length })
    : {};

  // Calculate age from date of birth
  const calculateAge = (birthDate) => {
    if (!birthDate) return '-';
    const birth = new Date(birthDate);
    const today = new Date();

    let years = today.getFullYear() - birth.getFullYear();
    let months = today.getMonth() - birth.getMonth();

    if (months < 0) {
      years--;
      months += 12;
    }

    return `${years} tahun ${months} bulan`;
  };

  const handleDelete = async (student) => {
    if (!(await confirmDialog(`Hapus data siswa ${student.full_name} (${student.nisn || student.id})?`))) return;
    try {
      await api.delete(`/users/${student.id}`);
      toast.success('Data siswa berhasil dihapus');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menghapus data siswa');
    }
  };

  return (
    <div className="space-y-6" data-testid="data-siswa-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Badge className="bg-[#006837]/10 text-[#006837] border-[#006837]/20 mb-2">
            <Users className="h-3 w-3 mr-1" /> Data Siswa
          </Badge>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Daftar Siswa</h1>
          <p className="text-sm text-slate-600 mt-1">
            {hasAdminView ? 'Semua siswa madrasah' : isWaliKelas ? 'Siswa di kelas Anda' : 'Siswa'}
          </p>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2" data-testid="aksi-data-siswa">
            <ExportTemplateButton
              endpoint="/students/import-template"
              kolom={KOLOM_DATA_SISWA}
              sheet="Data Siswa"
              filename="Template_Data_Siswa.xlsx"
              judul="Template Impor Data Siswa"
              testid="btn-export-template-siswa"
            />
            <ImportDataMasterButton
              onSelesai={(h) => { if (!h.tiruan && h.ringkasan.berhasil > 0) refresh(); }}
              endpoint="/students/import-kelengkapan"
              kolom={KOLOM_DATA_SISWA}
              sheet="Data Siswa"
              judul="Import Pelengkapan Data Siswa"
              namaData="siswa"
              testid="btn-import-siswa"
            />
            <UnduhDataSiswaButton
              tingkat={tingkatFilter}
              tingkatOptions={tingkatOptions}
              jumlahPerTingkat={jumlahPerTingkat}
              siswa={selectedClass === 'all' ? students : []}
              kelasMap={kelasMap}
              disabled={loading}
            />
          </div>
        )}
      </div>

      {/* Stat overview */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatBox icon={Users} label="Total" value={filtered.length} color="bg-slate-50 border-slate-200 text-slate-700" />
        <StatBox icon={UserCheck} label="Laki-laki" value={filtered.filter((s) => s.gender === 'L').length} color="bg-blue-50 border-blue-200 text-blue-700" />
        <StatBox icon={UserCheck} label="Perempuan" value={filtered.filter((s) => s.gender === 'P').length} color="bg-rose-50 border-rose-200 text-rose-700" />
        <StatBox icon={UserX} label="Mutasi Masuk" value={filtered.filter((s) => s.mutation_type === 'masuk').length} color="bg-amber-50 border-amber-200 text-amber-700" />
      </div>

      {/* Per-grade stat overview */}
      {hasAdminView && gradeStats.length > 0 && (
        <div className="space-y-3">
          {gradeStats.map(({ grade, total, laki, perempuan, mahad, avgCompleteness }) => (
            <Card key={grade}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <GraduationCap className="h-4 w-4 text-[#006837]" />
                  <h3 className="font-bold text-slate-900">Kelas {grade}</h3>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  <StatBox icon={Users} label="Total Siswa" value={total} color="bg-slate-50 border-slate-200 text-slate-700" />
                  <StatBox icon={UserCheck} label="Laki-laki" value={laki} color="bg-blue-50 border-blue-200 text-blue-700" />
                  <StatBox icon={UserCheck} label="Perempuan" value={perempuan} color="bg-rose-50 border-rose-200 text-rose-700" />
                  <StatBox icon={Home} label="Santri Mahad" value={mahad} color="bg-emerald-50 border-emerald-200 text-emerald-700" />
                  <StatBox
                    icon={ClipboardCheck}
                    label="% Kelengkapan Data"
                    value={`${avgCompleteness}%`}
                    color={
                      avgCompleteness >= 80 ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
                      avgCompleteness >= 50 ? 'bg-amber-50 border-amber-200 text-amber-700' :
                      'bg-rose-50 border-rose-200 text-rose-700'
                    }
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Cari nama atau NISN..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="search-siswa" />
          </div>
          {hasAdminView && (
            <Select value={tingkatFilter} onValueChange={setTingkatFilter}>
              <SelectTrigger data-testid="filter-tingkat"><SelectValue placeholder="Filter Tingkat" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Tingkat</SelectItem>
                {tingkatOptions.map((g) => <SelectItem key={g} value={String(g)}>Kelas {g}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {hasAdminView && (
            <Select value={selectedClass} onValueChange={setSelectedClass}>
              <SelectTrigger data-testid="filter-kelas"><SelectValue placeholder="Filter Kelas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Kelas</SelectItem>
                {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={genderFilter} onValueChange={setGenderFilter}>
            <SelectTrigger data-testid="filter-gender"><SelectValue placeholder="Filter Jenis Kelamin" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua</SelectItem>
              <SelectItem value="L">Laki-laki</SelectItem>
              <SelectItem value="P">Perempuan</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-slate-500">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-[#006837]" />
              Memuat data siswa...
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="siswa-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12 text-center">NO</TableHead>
                    <TableHead>NAMA</TableHead>
                    <TableHead>NISN</TableHead>
                    <TableHead>NISM</TableHead>
                    <TableHead>L/P</TableHead>
                    <TableHead>TEMPAT LAHIR</TableHead>
                    <TableHead>TGL LAHIR</TableHead>
                    <TableHead>UMUR</TableHead>
                    <TableHead>% DATA</TableHead>
                    <TableHead>KELAS</TableHead>
                    <TableHead>MAHAD</TableHead>
                    <TableHead>KAMAR</TableHead>
                    <TableHead>STATUS</TableHead>
                    <TableHead className="text-right">AKSI</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((s, i) => (
                    <TableRow key={s.id} data-testid={`siswa-row-${s.id}`}>
                      <TableCell className="text-center text-slate-500 font-mono">{i + 1}</TableCell>
                      <TableCell className="font-semibold">{s.full_name}</TableCell>
                      <TableCell className="font-mono text-xs">{s.nisn || '-'}</TableCell>
                      <TableCell className="font-mono text-xs">{s.nism || '-'}</TableCell>
                      <TableCell>
                        {s.gender === 'L' ? (
                          <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs">L</Badge>
                        ) : s.gender === 'P' ? (
                          <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-xs">P</Badge>
                        ) : <span className="text-slate-400 text-xs">-</span>}
                      </TableCell>
                      <TableCell className="text-sm">{s.birth_place || '-'}</TableCell>
                      <TableCell className="text-sm">
                        {s.birth_date ? formatWIB(s.birth_date, 'Date', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        }) : '-'}
                      </TableCell>
                      <TableCell className="text-sm">{calculateAge(s.birth_date)}</TableCell>
                      <TableCell>
                        <KelengkapanPopover
                          persen={s.completeness_percentage ?? 0}
                          muat={async () => (await api.get(`/students/${s.id}/kelengkapan`)).data.kelengkapan}
                          nama={s.full_name}
                          onLengkapi={() => setDetailStudent(s)}
                          testid={`btn-kelengkapan-siswa-${s.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        {s.class_name || '-'}
                        {s.rombel_semester_ini === false && (
                          <div className="text-[11px] text-amber-700" title="Proses lewat menu Naik Kelas / Proses Rombel">Belum masuk rombel semester ini</div>
                        )}
                      </TableCell>
                      <TableCell>
                        {s.santri_mahad ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">Santri</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs text-slate-500">Bukan</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{s.santri_mahad ? (s.kamar_mahad || '-') : '-'}</TableCell>
                      <TableCell>
                        {s.mutation_type === 'keluar' ? (
                          <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-xs">Mutasi Keluar</Badge>
                        ) : s.mutation_type === 'masuk' ? (
                          <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs">Mutasi Masuk</Badge>
                        ) : s.is_active === false ? (
                          <Badge variant="outline" className="text-xs">Nonaktif</Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">Aktif</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="outline" onClick={() => setDetailStudent(s)}
                            className="gap-1 border-[#006837]/40 text-[#006837] hover:bg-[#006837]/5"
                            data-testid={`detail-${s.id}`}>
                            <Eye className="h-3.5 w-3.5" /> Detail
                          </Button>
                          {canViewRiwayatUKS && (
                            <Button asChild size="sm" variant="outline" className="gap-1 border-rose-300 text-rose-700 hover:bg-rose-50">
                              <Link to={`/riwayat-uks/${s.id}`} data-testid={`riwayat-uks-${s.id}`}><HeartPulse className="h-3.5 w-3.5" /> Riwayat UKS</Link>
                            </Button>
                          )}
                          {canViewAccount && (
                            <Button size="sm" variant="outline" onClick={() => setAccountStudent(s)}
                              className="gap-1"
                              data-testid={`info-akun-${s.id}`}>
                              <KeyRound className="h-3.5 w-3.5" /> Info Akun
                            </Button>
                          )}
                          {canEdit && (
                            <Button size="sm" variant="ghost" onClick={() => setDetailStudent({ ...s, _autoEdit: true })}
                              className="gap-1 text-amber-700 hover:bg-amber-50"
                              data-testid={`edit-${s.id}`}>
                              <Pencil className="h-3.5 w-3.5" /> Edit
                            </Button>
                          )}
                          {canEdit && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(s)}
                              className="gap-1 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                              data-testid={`delete-${s.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" /> Hapus
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow><TableCell colSpan={14} className="text-center py-12 text-slate-500">
                      <GraduationCap className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                      <div className="font-semibold">Tidak ada data siswa</div>
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {detailStudent && (
        <StudentDetailDialog
          student={detailStudent}
          autoEdit={detailStudent._autoEdit}
          open={!!detailStudent}
          onClose={() => { setDetailStudent(null); refresh(); }}
        />
      )}

      {accountStudent && (
        <StudentAccountInfoDialog
          student={accountStudent}
          open={!!accountStudent}
          onClose={() => { setAccountStudent(null); refresh(); }}
        />
      )}
    </div>
  );
}

function StatBox({ icon: Icon, label, value, color }) {
  return (
    <div className={`rounded-xl border p-4 ${color}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide opacity-80">{label}</span>
        <Icon className="h-4 w-4 opacity-70" />
      </div>
      <div className="text-2xl font-extrabold tabular-nums mt-1">{value}</div>
    </div>
  );
}
